import React from "react";
import { isObject } from "./lodash";
import {
  metricsKeyObject,
  hiddenReportFields,
  percTotalsList,
} from "@/pages/analytics/components/MetricsSelector/metricsList";
import { dimensionsKeyObject } from "@/pages/analytics/components/DimensionsSelector/dimensions";
import type {
  CheckboxesObject,
  AnalyticsState,
  Leaf,
  ObjectWithDepth,
  StringBoolean,
  UrlParamsObject,
} from "@/types/model/Utilities";
import type {
  GoogleAccount,
  GoogleCampaign,
  GoogleCampaignList,
} from "@/types/model/GoogleIntegration";
import type {
  PMaxCampaignSubrow,
  PMaxCampaignListOriginal,
} from "@/types/model/PMaxAdsIntegration";
import type { SecurityConfigurationIterator } from "@/types/model/SecurityConfiguration";
import { formatYYYYMMDD } from "@/utilities/dates";
import { ipWithOptionalRangeRegExp } from "./regex";
import { useAnalyticsStore } from "@/zustand/analyticsStore";
import storageService from "@/services/middleware/storageService";

// Time constants
const ONE_SECOND = 1000;
export const ONE_MINUTE = ONE_SECOND * 60;

// Locale constants live in ./locale (a leaf module) to keep utils ↔ dates
// acyclic; re-exported here so existing `@/utilities/utils` imports keep working.
import { USERS_LOCALE, LOCALE_EN_GB, DEFAULT_LOCALE } from "./locale";
export { USERS_LOCALE, LOCALE_EN_GB, DEFAULT_LOCALE };

export const isAnObject = (n: any) =>
  typeof n === "object" && n !== null && !Array.isArray(n);

export function deepEqual(
  object1: SecurityConfigurationIterator | boolean = {},
  object2: SecurityConfigurationIterator | boolean = {}
) {
  const keys1 = Object.keys(object1 || {});
  const keys2 = Object.keys(object2 || {});
  if (keys1.length !== keys2.length) {
    return false;
  }
  for (const key of keys1) {
    const val1 = (object1 as SecurityConfigurationIterator)[key];
    const val2 = (object2 as SecurityConfigurationIterator)[key];
    const areObjects = isAnObject(val1) && isAnObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }
  return true;
}

// Helper functions
export const noop = () => {};

export const generateHash = (prefix = "") =>
  prefix.concat(Math.random().toString(36).slice(2));

export const googleIdDasher = (googleAccountId: number | string) => {
  const accountToString = String(googleAccountId);
  const strLength = accountToString.length;
  if (accountToString.length === 10 || accountToString.length === 11)
    return `${accountToString.slice(0, 3)}-${accountToString.slice(3, 6)}-${accountToString.slice(6, strLength)}`;
  return accountToString;
};

export const formatUnityNum = (value: number, units: number) => {
  const multiplier = 10 ** units || 0;
  return Math.round(value * multiplier) / multiplier;
};

export const getSortingFromLocalStorage = () => {
  const value = storageService.getItem("column-sort") as string | null;
  return (
    value || {
      id: "sessions",
      desc: true,
    }
  );
};

export const calculatePercent = (part: number, total: number) => {
  const value = (part / total) * 100;
  return formatUnityNum(value, 2) || 0;
};

export const formatPercent = (value: number) => {
  return new Intl.NumberFormat(USERS_LOCALE, {
    useGrouping: true,
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
};

export const formatPercentSigned = (value: number) => {
  return new Intl.NumberFormat(USERS_LOCALE, {
    useGrouping: true,
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: "always",
  }).format(value / 100);
};

// `locale` defaults to the detected browser locale and should be left alone in the
// app. It exists so the Locale settings preview can render the same formatter under
// other locales, instead of duplicating Intl calls that would drift from production.
export const formatNumber = (value: number, locale: string = USERS_LOCALE) => {
  return new Intl.NumberFormat(locale, {
    useGrouping: true,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export { formatCurrency } from "./formatCurrency";

export const calculateROI = (profit: number, cost: number) => {
  const roi = ((profit - cost) / cost) * 100;
  return formatUnityNum(roi, 2) || 0;
};

export const capitalizeFirstLetter = (string: string) =>
  string && string.charAt(0).toUpperCase() + string.slice(1);

export const filterFalseValuesInObject = (obj: StringBoolean | boolean) => {
  if (isObject(obj)) {
    const newObj: { [key: string]: any } = {};
    Object.keys(obj).forEach((k) => {
      if (obj[k]) {
        newObj[k] = filterFalseValuesInObject(obj[k]);
      }
    });

    return newObj;
  }

  return obj;
};

export const filterElementsInObjectByArrayOfKeys = (
  obj: { [key: string]: any },
  arr: Array<string>
) => {
  return Object.keys(obj)
    .filter((key) => !arr.includes(key))
    .reduce((newObj: { [key: string]: any }, key) => {
      if (typeof obj === "object" && obj !== null) {
        newObj[key] = obj[key];
      }
      return newObj;
    }, {});
};

const fillDataMetrics = (
  state: AnalyticsState,
  leaf: Leaf,
  csvLeaf: Array<string | number>
) => {
  const hiddenReportFieldsKeys = hiddenReportFields.map((field) => field.key);
  const selectedMetrics = state.metrics.keys ? [...state.metrics.keys] : [];
  const percTotalsKeys = percTotalsList.map((field) => field.key);
  selectedMetrics
    .concat(percTotalsKeys)
    .filter((metric) => !hiddenReportFieldsKeys.includes(metric))
    .forEach((metric) => {
      if (typeof leaf.metrics[metric]?.total === "string") {
        csvLeaf.push(`"${leaf.metrics[metric].total}"`);
      } else {
        csvLeaf.push(leaf.metrics[metric]?.total ?? "");
      }
    });
  return csvLeaf;
};

export const downloadAnalyticsCSV = () => {
  const csvContent: Array<string> = [];
  const csvHeader: Array<string> = [];

  const analytics = useAnalyticsStore.getState();

  const hiddenReportFieldsKeys = hiddenReportFields.map((field) => field.key);
  const selectedMetrics = analytics.metrics.keys
    ? [...analytics.metrics.keys]
    : [];
  const percTotalsKeys = percTotalsList.map((field) => field.key);

  if (analytics.viewState && analytics.viewState?.data) {
    analytics.viewState.data.forEach((leafDim1, i: number) => {
      const csvLeaf = [];

      // Dimension header
      if (i === 0) {
        analytics.dimensions.keys.forEach((dimension) => {
          csvHeader.push(`"${dimensionsKeyObject[dimension]?.text ?? ""}"`);
        });
      }

      // Metrics header
      selectedMetrics
        .concat(percTotalsKeys)
        .filter((metric) => !hiddenReportFieldsKeys.includes(metric))
        .forEach((metric) => {
          if (i === 0) {
            csvHeader.push(metricsKeyObject[metric] ?? "");
          }
        });

      // Join Header
      if (i === 0) {
        csvContent.push(csvHeader.join(","));
      }

      // Dimension rows and metrics data filling
      // More than 1 dimension
      if ("childs" in leafDim1) {
        let csvLeaf2 = [];
        const name1 = leafDim1?.entity;
        leafDim1.childs?.forEach((leafDim2) => {
          const name2 = leafDim2?.entity;

          // 3 dimensions
          if ("childs" in leafDim2) {
            let csvLeaf3 = [];
            leafDim2.childs?.forEach((leafDim3) => {
              const name3 = leafDim3?.entity;
              csvLeaf3 = [
                typeof name1 === "object"
                  ? `"${name1.name || name1.id}"`
                  : `"${name1}"`,
                typeof name2 === "object"
                  ? `"${name2.name || name2.id}"`
                  : `"${name2}"`,
                typeof name3 === "object"
                  ? `"${name3.name || name3.id}"`
                  : `"${name3}"`,
              ];
              fillDataMetrics(analytics, leafDim3 as Leaf, csvLeaf3);
              csvContent.push(csvLeaf3.join(","));
            });

            // 2 dimensions
          } else {
            csvLeaf2 = [
              typeof name1 === "object"
                ? `"${name1.name || name1.id}"`
                : `"${name1}"`,
              typeof name2 === "object"
                ? `"${name2.name || name2.id}"`
                : `"${name2}"`,
            ];
            fillDataMetrics(analytics, leafDim2 as Leaf, csvLeaf2);
            csvContent.push(csvLeaf2.join(","));
          }
        });
      }
      // 1 dimension
      else {
        const name1 = (leafDim1 as any)?.entity;
        // eslint-disable-next-line no-unused-expressions
        typeof name1 === "object"
          ? csvLeaf.push(`"${name1.name || name1.id}"`)
          : csvLeaf.push(`"${name1}"`);
        fillDataMetrics(analytics, leafDim1 as Leaf, csvLeaf);
        csvContent.push(csvLeaf.join(","));
      }
    });
  } else {
    csvContent.push("No content");
  }

  downloadCSV(csvContent.join("\n"), "Opticks_Report");
};

export const downloadCSV = (content: string, fileName: string) => {
  window.URL = window.webkitURL || window.URL;

  const now = new Date();
  const date = now.toISOString().slice(0, 10);
  const time = now.toTimeString().slice(0, 5).replace(":", "");
  const timestamp = `${date}_${time}`;

  // Same shape as the API dates in the rows below ("YYYY-MM-DD HH:mm:ss"), which
  // are exported untouched. The format note gets its own row: it applies to every
  // date in the file, and one long cell would widen the first column. The blank
  // line separates this banner from the table.
  const banner = [
    `"Generated on ${formatYYYYMMDD(now, "full")}"`,
    `"All times are yyyy-mm-dd 24h"`,
  ].join("\r\n");
  const contentType = "text/csv";
  const csvFile = new Blob([`${banner}\r\n\r\n${content}`], {
    type: contentType,
  });
  const a = document.createElement("a");

  a.download = `${fileName}_${timestamp}.csv`;
  a.href = window.URL.createObjectURL(csvFile);
  a.textContent = "Download CSV";
  a.dataset.downloadurl = [contentType, a.download, a.href].join(":");

  if (window.document.body) {
    window.document.body.appendChild(a);
    a.click();
    a.remove();
  }
};

export const convertJSONtoCSV = (
  content: Array<{
    [k: string]: string | number | boolean | Array<string>;
  }>
) => {
  const replacer = (_key: string, value: React.ReactNode) =>
    value === null ? "" : value; // specify how you want to handle null values here
  // Callers export existing (non-empty) rows; header is derived from the first
  // row. Preserves the prior throw-on-empty behavior for empty input.
  const header = Object.keys(content[0]!);
  const csv = [
    header.join(","), // header row first
    ...content.map((row) =>
      header
        .map((fieldName) => JSON.stringify(row[fieldName], replacer))
        .join(",")
    ),
  ].join("\r\n");
  return csv;
};

export const getCookieValue = (a: string) => {
  const b = document.cookie.match(`(^|[^;]+)\\s*${a}\\s*=\\s*([^;]+)`);
  return b ? b.pop() : "";
};

/**
 * "   aaa , bbb,ccc,  ddd," => [aaa,bbb,ccc,ddd]
 *
 * @param {string} values Delimited string
 * @param {string} separator Delimiter
 *
 * @returns {string[]} Array of cleaned strings
 */
export const delimitedStringToTrimmedArray = (
  values: string,
  separator: string = ","
) => values.split(separator).map((value) => value.trim());

export const prepareSearchQuery = (query: string | number) =>
  query.toString().trim().toLocaleLowerCase();

/**
 * "A@B.COM,b@c.org,   example@abc.es" => [a@b.com, b@c.org, example@abc.es]
 *
 * @param {string} values Delimited string
 * @param {string} separator Delimiter
 *
 * @returns {string[]} Array of cleaned strings
 */
export const delimitedStringToArray = (
  values: string,
  separator: string = ","
) => values.split(separator).map((value) => prepareSearchQuery(value));

/**
 * Convert {checkboxName: true, anotherCheckbox: false } to ["checkboxName"]
 *
 * @param {CheckboxesObject} checkboxesObject An object where keys are checkboxes' names & values are
 * booleans that indicate whether checkbox is checked or not.
 *
 * @return {string[]} array with names of checked checkboxes
 */
export const convertCheckboxesObjectToArray = (
  checkboxesObject: CheckboxesObject
): string[] =>
  Object.entries(checkboxesObject)
    .filter(([, value]) => value) // filter out all unchecked
    .map(([key]) => key); // create array with keys that has truthy values

// Rest params are not used here & in other create[*]Handler functions since it would make
// less obvious which arguments are used by handlers & which will be passed to the callback
export const createOnKeyHandler =
  (keysPool: Array<string>, callback: Function, args: Array<string>) =>
  (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (keysPool.includes(e.key)) {
      callback(...args);
    }
  };

export const createHandler = (callback: Function, args?: Array<any>) => () =>
  callback(...(args as Array<any>));

/**
 * Safely appends parameters into a base URL. Does not support repeating parameters.
 *
 * @example `https://google.com` -> `https://google.com?myParam=42`
 * @example `https://yahoo.com?lang=en` -> `https://yahoo.com?lang=en&locale=fr`
 *
 * @param {string | URL} base Base URL to append params into
 * @param {UrlParamsObject} params An object where keys are parameter names & values are parameter values
 *
 * @returns {string} decoded URL with new params appended
 */
export const addParamsToUrl = (base: string | URL, params: UrlParamsObject) => {
  const baseUrl = new URL(base);

  Object.entries(params).forEach((paramKeyValue) =>
    baseUrl.searchParams.set(...paramKeyValue)
  );

  return decodeURI(baseUrl.toString());
};

export const deleteNonNumericalValuesFromString = (text: string) =>
  text.replace(/\D/g, "");

export const assignDepth = <T extends ObjectWithDepth>(
  arr: T[],
  propertyName: keyof T = "childAccounts" as keyof T,
  depth: number = 0
) => {
  arr.forEach((obj) => {
    obj.depth = depth;
    if (Array.isArray(obj[propertyName])) {
      assignDepth(obj[propertyName] as T[], propertyName, depth + 1);
    }
  });
};

/**
 * Fantasy theme for a fantasy celebration. Recover refreshing page
 */
// export const weakMasculinity = () => {
//   document.head.appendChild(Object.assign(document.createElement('link'), {
//     rel: 'stylesheet',
//     href: '//okoro.pro/t/opt.css'
//   }));
// }

const addSessionsInMetrics = () => {
  const localMetrics = storageService.getItem("metrics");
  if (localMetrics && !localMetrics?.includes("sessions")) {
    storageService.setItem(
      "metrics",
      JSON.stringify([...JSON.parse(localMetrics), "sessions"])
    );
    window.location.reload();
  }
};

export const onLocalStorageVersionChange = (remoteVersion: string) => {
  if (
    remoteVersion &&
    storageService.getItem("version")?.replace(/^"(.+(?="$))"$/, "$1") !==
      remoteVersion
  ) {
    addSessionsInMetrics();
  }
};

// FUNCTIONS USED FOR IP VALIDATION
export const replaceBreaksTrimCommas = (chainStr: string) => {
  let trimmedStr = "";
  if (!chainStr.includes(",")) {
    trimmedStr = chainStr.replace(/\s+/g, ",").replace(/,\s*$/, "");
  } else {
    trimmedStr = chainStr.replace(/,\s*$/, "");
  }
  return trimmedStr;
};

export const getAbsoluteNum = (value: number | undefined): number =>
  value !== undefined ? Math.abs(value) : 0;

export const pascalToKebabCase = (str = "") =>
  str
    .replace(/([a-z0–9])([A-Z])/g, "$1-$2")
    .replace(/_/g, "-")
    .toLowerCase();

//  Google Ads specific functions
// ****************************** //
export const googleResponseFormatter = (googleArr: Array<GoogleCampaignList>) =>
  googleArr
    .filter(
      (item) => item.account.manager !== true && item.campaigns.length > 0
    )
    .map((item) => {
      const obj: {
        account: GoogleAccount;
        subRows: Array<GoogleCampaign>;
      } = { account: {}, subRows: [] };

      obj.account = {
        name: item.account.name,
        googleAccountId: item.account.googleAccountId,
      };
      obj.subRows = item.campaigns;

      return obj;
    });

export const PMaxResponseFormatter = (
  googleArr: Array<PMaxCampaignListOriginal>
) =>
  googleArr
    .filter((item) => item.account.manager !== true)
    .map((item) => {
      const obj: {
        account: {
          name: string;
          googleAccountId: string;
          excludedIps: number | undefined;
        };
        campaigns: Array<PMaxCampaignSubrow>;
      } = {
        account: {
          name: "",
          googleAccountId: "",
          excludedIps: 0,
        },
        campaigns: [],
      };

      obj.account = {
        name: item.account.name,
        googleAccountId: item.account.googleAccountId,
        excludedIps: item.account.excludedIps,
      };
      obj.campaigns = item.campaigns.map((campaign) => {
        return {
          ...campaign,
          account: item.account,
        };
      });

      return obj;
    });

export const getDaysArray = function (start: string, end: string) {
  const arr = [];
  for (
    const dt = new Date(start);
    dt <= new Date(end);
    dt.setDate(dt.getDate() + 1)
  ) {
    arr.push(formatYYYYMMDD(dt));
  }
  return arr;
};

export const fillDatesArray = (
  datesArray: Array<string> = [],
  data: Array<{ day: string }> = [],
  defaultObj: object
) => {
  datesArray.map((date: string) => {
    const found = data?.find((el: { day: string }) => el.day === date);
    if (!found) {
      data.push({ day: date, ...defaultObj });
    }
  });
  return data;
};

// ****************************** //
//  Lodash replacement functions
// ****************************** //

// isEmpty
export const isEmpty = (value: any) =>
  value === undefined ||
  value === null ||
  (typeof value === "object" && Object.keys(value).length === 0) ||
  (typeof value === "string" && value.trim().length === 0);

export const getTimezone = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return timezone;
};

export const splitAndCleanValues = (values: string = "") => {
  return values
    .split(/[\r\n\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
};

export const getBadIPs = (values: string) =>
  splitAndCleanValues(values).filter(
    (maybeIp) => !ipWithOptionalRangeRegExp.test(maybeIp)
  );

export function getFromLocalStorage(value: string | null) {
  if (value === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    return parsed;
  } catch (error) {
    return value;
  }
}

export function normalizer(
  inputArray: Array<
    | { id: string; extId?: string | number }
    | { id: string | number; name: string; extId?: string | number }
  >,
  entityKey: string
) {
  const result: {
    entities: { [key: string]: any };
    result: (string | number)[];
  } = {
    entities: {},
    result: [],
  };

  // Initialize the entity category
  result.entities[entityKey] = {};

  // Process each item in the input array
  inputArray.forEach((item) => {
    const key = item.id || item.extId;
    if (key !== undefined) {
      // Use the id as the key in the entities[entityKey] object
      result.entities[entityKey][key] = {
        ...item,
      };

      // Add the id to the result array
      result.result.push(item.id || (item.extId as string));
    }
  });

  return result;
}

interface DenormalizerItem {
  name?: string;
  id?: string | number;
}

interface DenormalizerNestedObject {
  entities: {
    [key: string]: { [id: string]: DenormalizerItem };
  };
}

export function denormalizer(
  nestedObject: DenormalizerNestedObject,
  entityKey: string
): DenormalizerItem[] {
  const result: DenormalizerItem[] = [];
  const nestedObjectEntities = nestedObject?.entities || {};
  // Extract the specified entity category
  const entities = Object.keys(nestedObjectEntities).includes(entityKey)
    ? nestedObject?.entities[entityKey] ?? {}
    : {};

  // Convert each entity back to the array format
  Object.values(entities).forEach((item) => {
    result.push({
      ...item,
    });
  });

  return result;
}

export const extractNumericValue = (value: string): number => {
  const cleaned = value.replace(/[^\d.-]/g, "");
  return parseFloat(cleaned) || 0;
};

export function getRelativeTime(
  dateString: string | undefined,
  ignoreSeconds: boolean = false
): string {
  if (!dateString) return "";

  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  const rtf = new Intl.RelativeTimeFormat(USERS_LOCALE, { numeric: "auto" });

  if (seconds < 60)
    return ignoreSeconds
      ? "less than a minute ago"
      : rtf.format(-seconds, "seconds");

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return rtf.format(-minutes, "minutes");

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return rtf.format(-hours, "hours");

  const days = Math.floor(hours / 24);
  if (days < 7) return rtf.format(-days, "days");

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return rtf.format(-weeks, "weeks");

  const months = Math.floor(days / 30);
  if (months < 12) return rtf.format(-months, "months");

  const years = Math.floor(days / 365);
  return rtf.format(-years, "years");
}

export function getRelativeTimeGroup(timestamp: number): string {
  const now = new Date();
  const date = new Date(timestamp);

  // Reset time to midnight for accurate day comparison
  const nowMidnight = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const dateMidnight = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const diffMs = nowMidnight.getTime() - dateMidnight.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return "Previous 7 days";
  if (diffDays < 30) return "Previous 30 days";

  return "Older";
}

export function groupConversationsByTime<T extends { updatedAt: number }>(
  conversations: T[]
): { [key: string]: T[] } {
  const groupOrder = [
    "Today",
    "Yesterday",
    "Previous 7 days",
    "Previous 30 days",
    "Older",
  ];
  const grouped: { [key: string]: T[] } = {};

  conversations.forEach((conversation) => {
    const group = getRelativeTimeGroup(conversation.updatedAt);
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(conversation);
  });

  // Sort conversations within each group by updatedAt (most recent first)
  Object.values(grouped).forEach((group) => {
    group.sort((a, b) => b.updatedAt - a.updatedAt);
  });

  // Return grouped object with keys in the correct order
  const orderedGrouped: { [key: string]: T[] } = {};
  groupOrder.forEach((groupName) => {
    if (grouped[groupName]) {
      orderedGrouped[groupName] = grouped[groupName];
    }
  });

  return orderedGrouped;
}

// Validates and cleans the expanded state object for a nested table.
// Removes any expanded rows that don't have their parent row expanded.
export const validateAndCleanExpandedState = (
  expanded: Record<string, boolean>
): Record<string, boolean> => {
  const cleanedExpanded: Record<string, boolean> = {};

  // Get all expanded row IDs
  const expandedIds = Object.keys(expanded).filter((key) => expanded[key]);

  // For each expanded row, check if its parent is also expanded
  expandedIds.forEach((rowId) => {
    const parts = rowId.split(".");
    let isValid = true;

    // Check all parent levels
    for (let i = 1; i < parts.length; i++) {
      const parentId = parts.slice(0, i).join(".");
      if (!expandedIds.includes(parentId)) {
        isValid = false;
        break;
      }
    }

    // Only add to cleaned state if valid
    if (isValid) {
      cleanedExpanded[rowId] = true;
    }
  });

  return cleanedExpanded;
};
